import { useMemo, useCallback, useState, useEffect, forwardRef } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import AssetTopRow from "../../../../common/features/asset/AssetTopRow.tsx";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import clsx from "clsx";
import BorrowOrOpenBookButton from "../../common/explore/BorrowOrOpenBookButton.tsx";
import AssetPanel from "../../common/asset-panel/AssetPanel.tsx";
import useExploreFetcher from "../../actions/explore/useExploreFetcher.tsx";
import useAssetAvailability, {
  BorrowStatus,
} from "../../actions/explore/activate/useAssetAvailability.tsx";
import useAssetReturn from "../../actions/explore/return/useAssetReturn.tsx";
import { useSearchParams } from "react-router-dom";
import { getSearchQuery } from "../query.ts";
import ContentResultsPanel from "../book-content/ContentResultsPanel.tsx";
import useFavorites from "../../actions/favorites/useFavorites.tsx";
import { makeSearchReturnDetail } from "../book-content/types.ts";
import OffScreen from "../../../../common/core-ui/OffScreen.tsx";
import ListTitleResultCard from "./ListTitleResultCard.tsx";
import { CompositeAsset } from "../../../../common/api/decimal/common-types/manageAssets.ts";
import useAccessibilityClaimsLink from "../../common/accessibility-claims/useAccessibilityClaimsLink.tsx";
import { PanelType as AssetPanelType } from "../../common/asset-panel/types.ts";

enum ExplorePanelType {
  Details = "details",
  ContentResults = "contents",
  Accessibility = "accessibility",
}

function InternalListTitleExploreResultCard(
  {
    asset,
    lazyLoad,
  }: {
    asset: CompositeAsset<"explore">;
    lazyLoad?: boolean;
  },
  ref: React.ForwardedRef<HTMLElement>
) {
  const intl = useIntl();
  const { getBorrowStatus } = useAssetAvailability(asset.explore);
  const borrowStatus = getBorrowStatus();
  const returnAsset = useAssetReturn(asset.explore);
  const [searchParams] = useSearchParams();
  const query = getSearchQuery(searchParams);
  const favorite = asset.favorite;
  const { favoritesMenuLink } = useFavorites(asset.manageAsset, favorite);
  const [openPanelInfo, setOpenPanelInfo] = useState<{
    panel: string;
    time: number;
  }>();

  const setOpenDetailsPanel = useCallback(() => {
    setOpenPanelInfo({ panel: ExplorePanelType.Details, time: Date.now() });
  }, []);
  const setOpenContentResultsPanel = useCallback(() => {
    setOpenPanelInfo({
      panel: ExplorePanelType.ContentResults,
      time: Date.now(),
    });
  }, []);
  const setOpenAccessibilityPanel = useCallback(() => {
    setOpenPanelInfo({
      panel: ExplorePanelType.Accessibility,
      time: Date.now(),
    });
  }, []);

  const a11yClaimsMenuLink = useAccessibilityClaimsLink(
    setOpenAccessibilityPanel
  );

  const popoverLinks = useMemo(() => {
    if (borrowStatus === BorrowStatus.UnBorrowable) {
      return [];
    }
    return [
      {
        label: intl.formatMessage({ defaultMessage: "Return" }),
        onClick: returnAsset,
        enabled:
          asset.explore.returnable && borrowStatus === BorrowStatus.Borrowed,
      },
      {
        label: intl.formatMessage({ defaultMessage: "Search Inside Book" }),
        onClick: () => setOpenContentResultsPanel(),
        enabled: true,
      },
      {
        ...a11yClaimsMenuLink,
        enabled: true,
      },
      {
        ...favoritesMenuLink,
        enabled: true,
      },
    ].filter((el) => el.enabled);
  }, [
    intl,
    setOpenContentResultsPanel,
    favoritesMenuLink,
    asset,
    borrowStatus,
    returnAsset,
    a11yClaimsMenuLink,
  ]);

  return (
    <>
      <ListTitleResultCard
        ref={ref}
        asset={asset}
        lazyLoad={lazyLoad}
        topRow={<AssetTopRow explore={asset.explore} links={popoverLinks} />}
        buttons={
          <>
            <BorrowOrOpenBookButton asset={asset.explore} />
            <BaseLinkOrButton
              onClick={setOpenDetailsPanel}
              className={clsx("vst-button-pill", "vst-button-variant-outline")}
              aria-label={intl.formatMessage(
                {
                  defaultMessage: "View Details for {title}",
                },
                { title: asset.manageAsset.title }
              )}
            >
              <FormattedMessage defaultMessage="View Details" />
            </BaseLinkOrButton>
          </>
        }
      >
        <OffScreen>
          <button
            tabIndex={-1}
            aria-hidden={true}
            data-library-return={makeSearchReturnDetail(
              "all",
              asset.manageAsset.vbid
            )}
            onClick={setOpenContentResultsPanel}
          />
        </OffScreen>
      </ListTitleResultCard>
      {openPanelInfo?.panel === ExplorePanelType.Details && (
        <AssetPanel
          explore={asset.explore}
          onRequestClose={() => setOpenPanelInfo(undefined)}
          key={openPanelInfo.time}
          startTab={AssetPanelType.Details}
        />
      )}
      {openPanelInfo?.panel === ExplorePanelType.ContentResults && (
        <ContentResultsPanel
          initialQuery={query}
          asset={asset.explore}
          onRequestClose={() => setOpenPanelInfo(undefined)}
          key={openPanelInfo.time}
          section={"all"}
        />
      )}
      {openPanelInfo?.panel === ExplorePanelType.Accessibility && (
        <AssetPanel
          explore={asset.explore}
          onRequestClose={() => setOpenPanelInfo(undefined)}
          key={openPanelInfo.time}
          startTab={AssetPanelType.Accessibility}
        />
      )}
    </>
  );
}

const InternalListTitleExploreResultCardRef = forwardRef(
  InternalListTitleExploreResultCard
);

function ListTitleExploreResultCard(
  {
    asset,
    lazyLoad,
  }: {
    asset: CompositeAsset<"explore" | "favorite">;
    lazyLoad?: boolean;
  },
  ref: React.ForwardedRef<HTMLElement>
) {
  const [updatedAsset, setUpdatedAsset] = useState(asset);
  const { activeFetcher } = useExploreFetcher(asset.explore);

  useEffect(() => {
    // Listen for activation/return fetcher and put updated asset from response
    // in local state. Does not get revalidated until page refresh.
    if (activeFetcher?.state === "loading" && activeFetcher.data?.ok) {
      setUpdatedAsset({ ...updatedAsset, explore: activeFetcher.data.asset! });
    }
  }, [activeFetcher, updatedAsset]);

  return (
    <InternalListTitleExploreResultCardRef
      ref={ref}
      asset={updatedAsset}
      lazyLoad={lazyLoad}
    />
  );
}

const Card = forwardRef(ListTitleExploreResultCard);

export default Card;
